import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Trash2, RefreshCw, Edit, User, Eye } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
// Removed search inputs and labels
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';

import { payerApi } from '../lib/api/payerApi';
import { PayerEditModal } from './PayerEditModal';

// Local types aligned with sample list payers response
type PayerItem = {
  PayerAccountId: string;
  Name: string;
  EmailAddress?: string;
  ExternalId1?: string;
  ExternalId2?: string;
  CreatedDate: string;
};

type PayerListResponseSample = {
  Payers: PayerItem[];
  TotalCount: number;
  RequestResult?: {
    ResultValue: string;
    ResultCode: string;
    ResultMessage: string;
  };
};

interface PayerListProps {
  onEditPayer?: (payer: PayerItem) => void;
}

export const PayerList: React.FC<PayerListProps> = ({ onEditPayer }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedPayer, setSelectedPayer] = useState<PayerItem | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  // Removed search/filter state

  // Query to fetch all payers
  const {
    data: payerData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['payers'],
    queryFn: () => payerApi.getAllPayers(),
  });

  // Delete payer mutation
  const deleteMutation = useMutation({
    mutationFn: payerApi.deletePayer,
    onSuccess: (data) => {
      toast.success(`Payer ${data.PayerID} deleted successfully`);
      queryClient.invalidateQueries({ queryKey: ['payers'] });
    },
    onError: (error: any) => {
      toast.error(`Failed to delete payer: ${error.response?.data?.ErrorMessage || error.message}`);
    },
  });

  // Clear all payers mutation
  const clearAllPayersMutation = useMutation({
    mutationFn: payerApi.clearAllPayers,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['payers'] });
      toast.success(`${data.DeletedCount} payers cleared successfully`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to clear payers: ${error.message}`);
    },
  });

  // Removed search handlers

  const handleDeletePayer = (payerId: string) => {
    deleteMutation.mutate(payerId);
  };

  const handleViewPayer = (payerId: string) => {
    navigate(`/payers/${payerId}`);
  };

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Loading Payers...
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-destructive">Error Loading Payers</CardTitle>
          <CardDescription>
            {error instanceof Error ? error.message : 'An unknown error occurred'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const payers = (payerData as unknown as PayerListResponseSample)?.Payers || [];

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Payers</h2>
          <p className="text-muted-foreground">
            {payers.length === 0
              ? 'No payers found'
              : `${payers.length} payer${payers.length === 1 ? '' : 's'} (${payerData?.TotalCount || 0} total)`
            }
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {payers.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear All
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear All Payers</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete all {payers.length} payers. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => clearAllPayersMutation.mutate()}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Clear All Payers
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Removed Search Filters section */}

      {/* Payers List */}
      {payers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <User className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Payers Found</h3>
            <p className="text-muted-foreground text-center">
              There are no payers in the system yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {payers.map((payer) => (
            <PayerCard
              key={payer.PayerAccountId}
              payer={payer}
              onEdit={(p) => {
                if (onEditPayer) {
                  onEditPayer(p);
                } else {
                  setSelectedPayer(p);
                  setIsEditOpen(true);
                }
              }}
              onDelete={handleDeletePayer}
              onView={handleViewPayer}
              isDeleting={deleteMutation.isPending}
            />
          ))}
        </div>
      )}

      <PayerEditModal
        payer={selectedPayer}
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setSelectedPayer(null);
        }}
      />
    </div>
  );
};

interface PayerCardProps {
  payer: PayerItem;
  onEdit?: (payer: PayerItem) => void;
  onDelete: (payerId: string) => void;
  onView: (payerId: string) => void;
  isDeleting: boolean;
}

const PayerCard: React.FC<PayerCardProps> = ({ payer, onEdit, onDelete, onView, isDeleting }) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {payer.Name}
            </CardTitle>
            <CardDescription>Account ID: {payer.PayerAccountId}</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onView(payer.PayerAccountId)}
            >
              <Eye className="h-4 w-4 mr-2" />
              View
            </Button>
            {onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(payer)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isDeleting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Payer</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete payer "{payer.Name}"? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDelete(payer.PayerAccountId)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* Basic Details */}
          <div className="space-y-2">
            <h4 className="font-semibold">Details</h4>
            <div className="space-y-1 text-sm">
              {payer.EmailAddress && (
                <div><span className="font-medium">Email:</span> {payer.EmailAddress}</div>
              )}
              {payer.ExternalId1 && (
                <div><span className="font-medium">External ID 1:</span> {payer.ExternalId1}</div>
              )}
              {payer.ExternalId2 && (
                <div><span className="font-medium">External ID 2:</span> {payer.ExternalId2}</div>
              )}
            </div>
          </div>
        </div>

        {/* Timestamps */}
        <div className="mt-4 pt-4 border-t text-xs text-muted-foreground">
          <div>Created: {new Date(payer.CreatedDate).toLocaleString()}</div>
        </div>
      </CardContent>
    </Card>
  );
};
